import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
    DEVICE_STATUS,
    InfraDeviceRow,
    REGISTRATION_MODE,
    TAB_TYPE,
} from '@/types/pages/storeManagement/registration/registrationTypes';
import { useRegisterDeviceCards } from '@/hooks/registration/useRegisterDeviceCards';
import { useRegistrationState } from '@/provider/registration/registrationContext';
import TagProgressCard from '@/components/registration/tagProgress/TagProgressCard';
import BaseFontIcon from '@/components/ui/BaseFontIcon';

type DeviceListPanelProps = {
    activeTab: TAB_TYPE;
};

const EMPTY_CONFIG = {
    [TAB_TYPE.AP]: {
        iconClass: 'fa-regular fa-router',
        title: 'No AP Registered',
        description: 'Add an AP first to continue registration',
    },
    [TAB_TYPE.RT]: {
        title: 'No Registered Devices',
    },
    [TAB_TYPE.TAG]: {
        iconClass: 'fa-regular fa-tag',
        title: 'No TAG Registered',
        description: 'Add TAG to continue registration',
    },
    [TAB_TYPE.NONE]: {
        title: 'No Registered Devices',
    },
} as {
    [key in keyof typeof TAB_TYPE]: {
        iconClass?: string;
        title: string;
        description?: string;
    };
};

const DeviceListPanel = ({ activeTab }: DeviceListPanelProps) => {
    const { t } = useTranslation('common');
    const { state, dispatch } = useRegistrationState();
    const { cards, isLoading } = useRegisterDeviceCards();

    const hasItems = cards.length > 0;
    const showEmptyLoading = isLoading && !hasItems;
    const showEmptyState = !isLoading && !hasItems;
    const showListOverlayLoading = isLoading && hasItems;

    const selectedDevice = useMemo(
        () => state.deviceInfoState.selectedInfraDevices[0] || null,
        [state.deviceInfoState.selectedInfraDevices],
    );

    const isInProgressStatus = (confirmed?: DEVICE_STATUS) => {
        return confirmed === DEVICE_STATUS.IN_PROGRESS || confirmed === DEVICE_STATUS.IN_PROGRESS_PENDING;
    };

    const handleCardClick = (item: InfraDeviceRow) => {
        const isSelected = selectedDevice?.mac === item.mac;

        if (activeTab === TAB_TYPE.TAG) return;

        if (isSelected || state.registrationMode === REGISTRATION_MODE.NORMAL_MODE) {
            dispatch({
                type: 'SET_TABLE_STATE',
                payload: { selectedInfraDevices: [] },
            });
            return;
        }

        dispatch({
            type: 'SET_TABLE_STATE',
            payload: { selectedInfraDevices: [item] },
        });
    };

    const getConfirmedLabel = (confirmed?: DEVICE_STATUS) => {
        if (confirmed === DEVICE_STATUS.CONFIRMED) return t('Confirmed');
        if (confirmed === DEVICE_STATUS.UN_CONFIRMED) return t('Unconfirmed');
        if (isInProgressStatus(confirmed)) return t('In Progress');

        return t('Unknown');
    };

    const getCardBadgeLabel = (item: InfraDeviceRow) => {
        const shouldShowApLockStatus = activeTab === TAB_TYPE.RT && item.isAP;

        if (shouldShowApLockStatus) {
            return item.isLocked ? t('LOCKED') : t('UNLOCKED');
        }

        return getConfirmedLabel(item.confirmed);
    };

    const getConfirmedSx = (item: InfraDeviceRow) => {
        const confirmed = item.confirmed;
        if (item.isAP && activeTab === TAB_TYPE.RT) {
            if (item.isLocked) {
                return {
                    color: '#B91C1C',
                    bgcolor: '#FEE2E2',
                    borderColor: '#FCA5A5',
                };
            }
        }
        if (confirmed === DEVICE_STATUS.CONFIRMED) {
            return {
                color: '#15803D',
                bgcolor: '#DCFCE7',
                borderColor: '#BBF7D0',
            };
        }

        if (confirmed === DEVICE_STATUS.UN_CONFIRMED) {
            return {
                color: '#B45309',
                bgcolor: '#FEF3C7',
                borderColor: '#FDE68A',
            };
        }

        if (isInProgressStatus(confirmed)) {
            return {
                color: '#1D4ED8',
                bgcolor: '#DBEAFE',
                borderColor: '#BFDBFE',
            };
        }

        return {
            color: '#475569',
            bgcolor: '#F1F5F9',
            borderColor: '#E2E8F0',
        };
    };

    if (showEmptyLoading) {
        return (
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                }}
            >
                <CircularProgress size={24} />

                <Typography
                    variant="body2"
                    sx={{
                        color: '#64748B',
                        fontWeight: 600,
                    }}
                >
                    {t('Loading Devices')}
                </Typography>
            </Box>
        );
    }

    if (showEmptyState) {
        const emptyState = EMPTY_CONFIG[activeTab];
        return (
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 3,
                    py: 4,
                }}
            >
                <Box
                    sx={{
                        maxWidth: 280,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                    }}
                >
                    {emptyState?.iconClass && (
                        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <BaseFontIcon className={emptyState.iconClass} size={40} />
                        </Box>
                    )}

                    {emptyState?.title && (
                        <Typography
                            sx={{
                                fontSize: 15,
                                fontWeight: 800,
                                color: '#334155',
                                mb: 0.5,
                            }}
                        >
                            {t(emptyState.title)}
                        </Typography>
                    )}

                    {emptyState?.description && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#94A3B8',
                                textAlign: 'center',
                                fontSize: 13,
                                fontWeight: 600,
                                lineHeight: 1.5,
                            }}
                        >
                            {t(emptyState.description)}
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                position: 'relative',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                gap: 0.75,
            }}
        >
            {state.registrationMode === REGISTRATION_MODE.INSTALLATION_MODE &&
                activeTab === TAB_TYPE.TAG &&
                state.tagState.isOnProgress && (
                    <TagProgressCard
                        compact
                        confirmedTags={state.tagState.status.confirmed}
                        totalTags={state.tagState.status.total}
                    />
                )}

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    mt: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',

                    opacity: showListOverlayLoading ? 0.55 : 1,
                    filter: showListOverlayLoading ? 'grayscale(0.12)' : 'none',
                    pointerEvents: showListOverlayLoading ? 'none' : 'auto',
                    userSelect: showListOverlayLoading ? 'none' : 'auto',
                    transition: 'opacity 160ms ease, filter 160ms ease',
                    '&::-webkit-scrollbar': {
                        width: 5,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#CBD5E1',
                        borderRadius: 999,
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                    },
                }}
            >
                {cards.map((item, index) => {
                    const selected = selectedDevice?.mac === item.mac;
                    const isInProgress = isInProgressStatus(item.confirmed);
                    const confirmedSx = getConfirmedSx(item);

                    return (
                        <Box
                            key={`${activeTab}-${item.mac}-${index}`}
                            sx={{
                                position: 'relative',
                                flexShrink: 0,
                                overflow: 'hidden',
                                borderRadius: 2,
                            }}
                        >
                            <Box
                                onClick={() => handleCardClick(item)}
                                role="button"
                                tabIndex={0}
                                sx={{
                                    position: 'relative',
                                    p: 1.25,
                                    borderRadius: 2,
                                    border: '2px solid',
                                    borderColor: selected ? '#2563EB' : '#E2E8F0',
                                    bgcolor: selected ? '#EFF6FF' : '#FFFFFF',
                                    boxShadow: selected ? '0 0 0 1px #2563EB' : '0 1px 2px rgba(15, 23, 42, 0.06)',
                                    cursor: 'pointer',
                                    WebkitTapHighlightColor: 'transparent',
                                    transition:
                                        'border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease, transform 160ms ease',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1.25,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: 800,
                                            letterSpacing: 0.35,
                                            color: '#475569',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {item.deviceTypeDisplay}
                                    </Typography>

                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: isInProgress ? 0.75 : 0,
                                            px: 1,
                                            py: 0.45,
                                            borderRadius: 999,
                                            border: '1px solid',
                                            fontSize: 11,
                                            fontWeight: 800,
                                            lineHeight: 1,
                                            whiteSpace: 'nowrap',
                                            ...confirmedSx,
                                        }}
                                    >
                                        {isInProgress && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: '50%',
                                                    bgcolor: 'currentColor',
                                                    animation: 'device-card-status-dot 1s ease-in-out infinite',

                                                    '@keyframes device-card-status-dot': {
                                                        '0%': {
                                                            opacity: 0.35,
                                                            transform: 'scale(0.85)',
                                                        },
                                                        '50%': {
                                                            opacity: 1,
                                                            transform: 'scale(1.15)',
                                                        },
                                                        '100%': {
                                                            opacity: 0.35,
                                                            transform: 'scale(0.85)',
                                                        },
                                                    },
                                                }}
                                            />
                                        )}

                                        {getCardBadgeLabel(item)}
                                    </Box>
                                </Box>

                                <Typography
                                    sx={{
                                        mt: 0.75,
                                        fontSize: 15,
                                        fontWeight: 800,
                                        color: '#0F172A',
                                        letterSpacing: 0.2,
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    {item.mac}
                                </Typography>

                                <Box sx={{ mt: 0.75 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 10.5,
                                            fontWeight: 700,
                                            color: '#94A3B8',
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.35,
                                            lineHeight: 1,
                                        }}
                                    >
                                        {t('Parent MAC')}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.4,
                                            fontSize: 12.5,
                                            fontWeight: 700,
                                            color: '#475569',
                                            lineHeight: 1.3,
                                            wordBreak: 'break-all',
                                        }}
                                    >
                                        {item.parent_mac || '-'}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        mt: 0.75,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1.25,
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.65,
                                            minWidth: 0,
                                            color: item.edStatusColor || '#64748B',
                                        }}
                                    >
                                        <Box
                                            component="span"
                                            sx={{
                                                width: 7,
                                                height: 7,
                                                borderRadius: '50%',
                                                flexShrink: 0,
                                                bgcolor: item.edStatusColor || '#94A3B8',
                                            }}
                                        />

                                        <Typography
                                            component="span"
                                            sx={{
                                                fontSize: 12.5,
                                                fontWeight: 700,
                                                color: 'inherit',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {item.edStatusDisplay}
                                        </Typography>
                                    </Box>

                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: 11.5,
                                            fontWeight: 600,
                                            color: '#64748B',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {item.edStatusLocalTime}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default DeviceListPanel;
